import React, { memo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { ProfileData } from "@/hooks/useProfiles";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useToast } from "@/hooks/use-toast";
import { trackProfileAction } from "@/utils/analytics";
import { LikeButton } from "./LikeButton";

interface ProfileActionsBarProps {
  connected?: boolean;
  onConnectClick?: () => void;
  profile?: ProfileData;
  isLiked?: boolean;
  onLikeChange?: (newLikeState: boolean, newCount?: number) => void;
}

const ProfileActionsBar: React.FC<ProfileActionsBarProps> = ({
  profile,
  isLiked: propIsLiked,
  onLikeChange,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Track profile action when like changes
  const handleLikeChange = useCallback(
    (liked: boolean, count: number) => {
      if (profile?.id) {
        // Track analytics
        trackProfileAction(liked ? "like" : "unlike", profile.id);

        // Show feedback toast for successful like actions
        if (liked && user) {
          toast({
            title: "Profile liked!",
            description: `You liked ${
              profile.display_name || profile.username || "this profile"
            }`,
            duration: 2000,
          });
        }
      }

      // Call parent callback with both like state and count
      if (onLikeChange) {
        onLikeChange(liked, count);
      }
    },
    [
      profile?.id,
      profile?.display_name,
      profile?.username,
      onLikeChange,
      user,
      toast,
    ]
  );

  if (!profile) {
    return (
      <div className="flex items-center h-full">
        <Button variant="secondary" size="default" className="h-full" disabled>
          <Heart className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center h-full">
      <LikeButton
        profileId={profile.id}
        className="h-full"
        onLikeChange={handleLikeChange}
        showCount={false}
        variant="secondary"
        iconOnly={true}
        size="icon"
      />
    </div>
  );
};

export default memo(ProfileActionsBar);
